import { Box, Button, Card, Grid, Group, rem, Stack, Text, Title } from '@mantine/core';
import { modals } from '@mantine/modals';
import { getLogger } from 'loglevel';
import { useCallback } from 'react';

import { ShoppingBag } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { MenuPopover } from '@apple/ui/shell/components/MenuPopover';
import { useLogger } from '@apple/utils/logging/useLogger';

import { useCartIdContext } from '@apple/features/cart/contexts/cart-id';

import { useCartItemCount } from '../hooks/useCartItemCount';
import { useResetCart } from '../hooks/useResetCart';

const log = getLogger('cart-menu');

export function CartMenu({
	onCartCleared,
	onViewCartClicked,
}: {
	onCartCleared: () => void;
	onViewCartClicked: () => void;
}) {
	const { t } = useTranslation('shop');
	const cartId = useCartIdContext();
	const resetCart = useResetCart();
	const cartItemCount = useCartItemCount();

	useLogger({
		name: 'CartMenu',
		props: [
			{
				cartId,
				cartItemCount,
			},
		],
		log,
	});

	const handleClearCart = useCallback(() => {
		if (cartItemCount.isSuccess && cartItemCount.data === 0) {
			resetCart.mutate(undefined, {
				onSuccess: onCartCleared,
			});
			return;
		}

		modals.openConfirmModal({
			modalId: 'clear-cart',
			title: t('bag.clearModal.title'),
			children: <Text>{t('bag.clearModal.description')}</Text>,
			labels: {
				confirm: t('bag.clearModal.continue'),
				cancel: t('common:buttons.cancel'),
			},
			onConfirm: () =>
				resetCart.mutate(undefined, {
					onSuccess: onCartCleared,
				}),
		});
	}, [t, onCartCleared, cartItemCount.data, cartItemCount.isSuccess, resetCart]);

	return (
		<MenuPopover
			icon={ShoppingBag}
			iconTestId='cart-menu'
			loading={cartItemCount.isLoading}
			tooltipLabel={t('bag.labels.bag')}
			indicatorCount={cartItemCount.data}
			indicatorDisabled={cartItemCount.data <= 0}
		>
			<Card.Section
				m={0}
				p='md'
				style={{
					borderRadius: `${rem(24)} ${rem(24)} 0 0`,
				}}
			>
				<Grid align='center'>
					<Grid.Col offset={3} span={6}>
						<Title order={3} ta='center'>
							{t('bag.labels.bag')}
						</Title>
					</Grid.Col>

					<Grid.Col span={3}>
						<Group justify='flex-end'>
							<Button
								size='compact-sm'
								variant='subtle'
								fw={600}
								c='red'
								onClick={handleClearCart}
								disabled={cartItemCount.data === 0}
							>
								{t('bag.buttons.clearBag')}
							</Button>
						</Group>
					</Grid.Col>
				</Grid>
			</Card.Section>

			<Card.Section m='xs'>
				<Group justify='center' mt='xs'>
					{cartItemCount.data > 0 ? (
						<Stack>
							<Box>
								<Text>
									{t('bag.labels.itemsCountMessage', {
										itemsCount: cartItemCount.data,
									})}
								</Text>
							</Box>
							<Button data-testid='view-bag' w={'14em'} onClick={onViewCartClicked}>
								{t('bag.buttons.viewBag')}
							</Button>
						</Stack>
					) : (
						<Box mt='xs'>
							<Text>{t('bag.labels.noItemsMessage')}</Text>
						</Box>
					)}
				</Group>
			</Card.Section>
		</MenuPopover>
	);
}
